
import { Vue, Prop, Component } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";
import BarcodeOrderModule from "@/store/modules/barcodeSystem/barcodeOrder";
import InventoryModule from "@/store/modules/barcodeSystem/inventory";
import { BarcodeOrderItemListItem } from "@/models/api/barcodeSystem/barcodeOrders/get";
import { formatDateWithTime, formatBool } from "@/utils";
import { UpdateBarcodeOrderItemRequest } from "@/models/api/barcodeSystem/barcodeOrders/updateBarcodeOrderItem";
import notifications from "@/lib/notifications";
import BarcodeOrderItemUpdate from "@/components/barcodeSystem/barcodeOrder/BarcodeOrderItemUpdate.vue"
import permissions from "@/security/permissions";
import draggable from "vuedraggable";
import RightClickMenu from "@/components/widgets/RightClickMenu.vue";

const barcodeOrderModuleState = getModule(BarcodeOrderModule);
const inventoryModuleState = getModule(InventoryModule);

@Component({
  components: {
    BarcodeOrderItemUpdate,
    RightClickMenu,
    draggable
  }
})
export default class BarcodeOrderItemTable extends Vue {
  @Prop({ default: false })
  loading: boolean;

  @Prop({ default: null })
  tableMenuItem: Array<any>; 

  rightClickLocation: any = null;
  draggingData: boolean = false;
  showUpdateOrderItemModal: boolean = false;
  updateItemLoading: boolean = false;
  updateBarcodeOrderItemRequest: UpdateBarcodeOrderItemRequest = null;
  updatingItemValidationMessages: string[] = null;

  get isAgentObject() : boolean
  {
    return barcodeOrderModuleState.currentBarcodeOrder ? barcodeOrderModuleState.currentBarcodeOrder.isAgentObject : false;
  }
  
  get filteredMenuItems() {
    return this.selectedItems && this.selectedItems.length == 1 ? 
      this.tableMenuItem.filter(x => x.type != "viewInventoryItem" || this.selectedItems[0].inventoryItemId) :
      null;
  }

  get barcodeOrderItems(): Array<BarcodeOrderItemListItem> {
    return barcodeOrderModuleState.currentBarcodeOrderItems;
  }

  get selectedItemCount(): number {
    return barcodeOrderModuleState.getSelectedBarcodeOrderItemCount;
  }

  get selectedItems(): Array<any> {
    return barcodeOrderModuleState.getSelectedBarcodeOrderItems;
  }

  get totalItems() {
    if (barcodeOrderModuleState.currentBarcodeOrderItems) {
      return barcodeOrderModuleState.currentBarcodeOrderItems.length
    } else {
      return 0;
    }
  }
  
  get isUpdateable() : boolean
  {
    return this.updateBarcodeOrderItemRequest
      ? this.updateBarcodeOrderItemRequest.isUpdateable 
      : false;
  }

  selectedHasInventoryItem() {
    return this.selectedItemCount == 1 && this.selectedItems[0].inventoryItemId;
  }

  async updateItemPrompt(data)
  {
    this.updateBarcodeOrderItemRequest = null;
    this.updateItemLoading = true;
    this.showUpdateOrderItemModal=true;
    this.updateBarcodeOrderItemRequest = await barcodeOrderModuleState.previewUpdateBarcodeOrderItem(data);
    this.updateItemLoading = false;
  }

  async updateOrderItem() {
    if(this.validateOrderItemUpdate())
    {
      this.updateItemLoading = true;
      try {
        await barcodeOrderModuleState.updateBarcodeOrderItem(
          this.updateBarcodeOrderItemRequest
        );
        notifications.success("Barcode Order Item Updated", "Barcode Order Item successfully updated.");
        this.$emit("reload");
        this.closeModals();
      } catch (ex) {
        notifications.error("Updating Barcode Order Item", "Failed to update barcode order item.", ex);
      } finally {
        this.updateItemLoading = false;
      }
    }
  }

  validateOrderItemUpdate() : boolean {
    let valid = this.updateBarcodeOrderItemRequest != null;
    this.updatingItemValidationMessages = null;

    if(!valid)
      return false;

    if(this.updateBarcodeOrderItemRequest.isDamaged && this.updateBarcodeOrderItemRequest.quantityDamaged <= 0) {
      valid = false;
      if(!this.updatingItemValidationMessages)
        this.updatingItemValidationMessages = [];
      this.updatingItemValidationMessages.push('All damaged items must have a quantity damaged greater than 0.'); 
    }
    if(this.updateBarcodeOrderItemRequest.isDamaged && !this.updateBarcodeOrderItemRequest.damageCode) {
      valid = false;
      if(!this.updatingItemValidationMessages)
        this.updatingItemValidationMessages = [];
      this.updatingItemValidationMessages.push('All damaged items must have a selected damage code.');
    }
    if(this.updateBarcodeOrderItemRequest.quantityReceived > this.updateBarcodeOrderItemRequest.quantity) {
      valid = false;
      if(!this.updatingItemValidationMessages)
        this.updatingItemValidationMessages = [];
      this.updatingItemValidationMessages.push('Quantity received cannot be greater than an items quantity.');
    }
    if(this.updateBarcodeOrderItemRequest.quantityLoaded > this.updateBarcodeOrderItemRequest.quantity) {
      valid = false;
      if(!this.updatingItemValidationMessages)
        this.updatingItemValidationMessages = [];
      this.updatingItemValidationMessages.push('Quantity loaded cannot be greater than an items quantity.');
    }
    if(this.updateBarcodeOrderItemRequest.quantityDelivered > this.updateBarcodeOrderItemRequest.quantity) {
      valid = false;
      if(!this.updatingItemValidationMessages)
        this.updatingItemValidationMessages = [];
      this.updatingItemValidationMessages.push('Quantity delivered cannot be greater than an items quantity.');
    }
    return valid;
  }

  getDamagedMessage(orderItem) : string {
    let damageMessage = formatBool(orderItem.isDamaged);
    if(orderItem && orderItem.isDamaged) {
      if(orderItem.damageCode)
        damageMessage = damageMessage + ' ' + orderItem.damageCode + '';
      damageMessage = damageMessage + '(' + orderItem.quantityDamaged + ')';
    }
    return damageMessage;
  }

  formatDateWithTime(val)
  {
    return formatDateWithTime(val)
  }

  formatBool(val)
  {
    return formatBool(val);
  }

  getComponentData(rowData) {
    return {
      on: {
        click: () => this.onRowClick(rowData),
        contextmenu: $event => this.onRightClicked(rowData, $event),
        dblclick: () => this.onDoubleClicked(rowData)
      }
    };
  }

  onRightClicked(rowData, event) {
    event.preventDefault();
    if (!rowData.selected) {
      this.unselectAll();
      barcodeOrderModuleState.switchBarcodeOrderItemSelectionStatus(rowData.id);
    }
    this.rightClickLocation = { x: event.x, y: event.y };
  }

  clearRightClick() {
    this.rightClickLocation = null;
    this.draggingData = false;
  }

  onDoubleClicked(data) {
    this.updateItemPrompt(data);
  }

  onRowClick(data) {
    this.unselectAll();
    barcodeOrderModuleState.switchBarcodeOrderItemSelectionStatus(data.id);
  }

  clickMenu(item) {
    switch(item.type)
    {
      case "viewTransactions":
        if(this.selectedItems != null && this.selectedItems.length == 1) {       
          this.$emit("viewItemTransactions", this.selectedItems[0].id);
        }
        break;
      case "viewInventoryItem":
        if(this.selectedItems != null && this.selectedItems.length == 1) {       
          this.viewInventoryItem(this.selectedItems[0].inventoryItemId);
        }
        break;
      case "updateItem":
        if(this.selectedItems != null && this.selectedItems.length == 1) {       
          this.updateItemPrompt(this.selectedItems[0]);
        }
        break;
    }
    this.clearRightClick();
  }

  unselectAll() {
    let items = this.selectedItems;

    items.forEach(item => {
      barcodeOrderModuleState.switchBarcodeOrderItemSelectionStatus(item.id);
    });
  }

  closeModals()
  {
    this.showUpdateOrderItemModal=false;
  }

  viewInventoryItem(id){
    if(this.hasPermission(permissions.barcodeSystem.inventory.view))
    {
      inventoryModuleState.viewInventoryItemShortcutAction({selectedItems: [{id: id}]})
    }
  }
}
